import { FC, ReactNode, useRef } from 'react'
import classNames from 'classnames'

import Button from './forms/Button'
import { planPrice } from 'common/utils/planPrice'
import useWindowSize from './util/useWindowSize'
import { TPlanSummary } from 'common/cms-types'
import { useRouter } from 'next/router'
import useShoppingBag from 'common/useShoppingBag'
import { getApi } from 'common/api-common'
import { planToEvent } from 'common/utils/planToEvent'
import { useViewTracker } from 'common/useViewTracker'
import OurAddress from './OurAddress'

export type SubscriptionPricingType = {
  hideTitle?: boolean
  className?: string
  showLink?: boolean
  showCTABtnInRow?: boolean
  hideCTA?: boolean
  locationClassName?: boolean
  planSummary: TPlanSummary
  priceClass?: string
  monthClass?: string
  au?: boolean
  rightButton?: ReactNode
  hideAddress?: boolean
  buttonAlign?: 'center' | 'start'
  hideRegister?: boolean
  priceSubClass?: string
}

const SubscriptionPricing: FC<SubscriptionPricingType> = ({
  au,
  className,
  buttonAlign = 'center',
  rightButton,
  hideAddress,
  hideCTA,
  hideRegister,
  hideTitle,
  locationClassName = 'fw-semibold  gap-1 fs-fluid-24-26 mb-4 color-pink2',
  monthClass = 'text-xl',
  planSummary,
  priceClass = 'fw-bold fs-28',
  priceSubClass = 'fs-16 fw-semibold',
  showCTABtnInRow = false,
}) => {
  const router = useRouter()
  const { addPlan } = useShoppingBag()
  const containerRef = useRef<HTMLDivElement>(null)
  useViewTracker(containerRef, () => {
    if (!hideCTA && planSummary) {
      getApi().trackEvent('view_item_ga4', planToEvent(planSummary, 'month'))
    }
  })
  const size = useWindowSize()

  if (!planSummary) {
    return null
  }
  const monthlyPriceMonthly = planPrice.monthly[planSummary.slug]
  const monthlyPriceAnnual = planPrice.monthlyOnYearlyPlan(planSummary.slug)
  const onBuyClick = () => {
    addPlan(planSummary.slug, 'year', planSummary)
    router.push('/checkout')
  }
  return (
    <div ref={containerRef} className={className}>
      {!hideTitle && (
        <>
          {!hideRegister && (
            <h3 className='fw-semibold fs-fluid-24-26 mb-0'>
              Register your virtual office address{' '}
            </h3>
          )}
          {!hideAddress && <OurAddress className={locationClassName} au={au} />}
        </>
      )}
      {/*fs always 28 and 16*/}
      {!au && (
        <div
          className={classNames('d-flex flex-column', {
            'justify-content-lg-between align-items-lg-end flex-lg-row ':
              showCTABtnInRow,
          })}
        >
          <div className='d-flex flex-column'>
            <div className={priceClass}>
              {monthlyPriceAnnual.includes('Under') ? '' : 'From '}
              {monthlyPriceAnnual}
              <span className={monthClass}>/month</span>
            </div>
            <div className={priceSubClass}>
              Billed annually, or {monthlyPriceMonthly} per month when billed monthly.
            </div>
          </div>
          {!hideCTA && (
            <>
              {rightButton ? (
                <div
                  className={classNames(
                    showCTABtnInRow && size.isLgOrHigher
                      ? 'd-flex gap-2 justify-content-end'
                      : 'd-flex gap-2',
                  )}
                >
                  <Button
                    className={classNames('w-100 px-22 w-lg-auto mt-5', {
                      'mt-lg-8': !showCTABtnInRow,
                      'mt-lg-n1': showCTABtnInRow && size.isXlOrHigher,
                    })}
                    onClick={onBuyClick}
                  >
                    Buy Now
                  </Button>
                  {rightButton}
                </div>
              ) : (
                <Button
                  className={classNames(
                    `w-100 px-22 w-lg-auto mt-5 align-self-lg-${buttonAlign}`,
                    {
                      'mt-lg-8': !showCTABtnInRow,
                      'mt-lg-n1': showCTABtnInRow && size.isXlOrHigher,
                    },
                  )}
                  onClick={onBuyClick}
                >
                  Buy Now
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default SubscriptionPricing
